import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { fetchNodeAggregate } from '../../api';
import { PANEL_IDS } from '../../config/panelRegistry';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import useNodeCatalog from '../../hooks/useNodeCatalog';
import InfoHint from '../InfoHint';
import useNodeRequest from '../nodes/useNodeRequest';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import AllCategoriesTrend from './AllCategoriesTrend';
import { ErrorText, MetricToggle, MutedText } from './callTypesControls';
import { categoryFilter, findCallCharts, formatCount, formatPercent, pairFilter, readCategoryRows, resolvePair } from './callTypesModel';
import CategoryList from './CategoryList';
import EmployeeComparisonTrend from './EmployeeComparisonTrend';
import EmployeeQualityCard from './EmployeeQualityCard';
import PairCallsCard from './PairCallsCard';
import PairTrendChart from './PairTrendChart';
import useEmployeeBreakdown from './useEmployeeBreakdown';
import SectionGrid from '../layout/SectionGrid';
import { buildTabDefaults } from '../../config/sectionLayouts';

const CALL_PATHS_DEFAULTS = buildTabDefaults('call-paths');

// Разбор появляется только после выбора типа или итога; в режиме правки на его месте заглушка,
// чтобы и ему можно было задать место.
const DRILLDOWN_PLACEHOLDER = [{
  id: 'ui:call-paths.drilldown',
  hint: 'Здесь появится разбор, когда пользователь выберет тип или итог звонка'
}];

const MODES = [
  { id: 'types', label: 'По типам' },
  { id: 'outcomes', label: 'По итогам' }
];

const OVERVIEW_PANELS = {
  types: { panelId: PANEL_IDS['ui:call-paths.types'], emptyText: 'Нет определённых типов за выбранный период.' },
  outcomes: { panelId: PANEL_IDS['ui:call-paths.outcomes'], emptyText: 'Нет определённых итогов за выбранный период.' }
};

const fetchOverview = async ({ typeChart, outcomeChart, slice }) => {
  const aggregateOf = (chart) => fetchNodeAggregate({ ...slice, node_id: chart.nodeId, output_key: chart.outputKey });
  const [types, outcomes] = await Promise.all([aggregateOf(typeChart), aggregateOf(outcomeChart)]);

  return {
    types: readCategoryRows(types),
    outcomes: readCategoryRows(outcomes),
    typesAnalyzed: Number(types?.denominatorCalls ?? 0),
    outcomesAnalyzed: Number(outcomes?.denominatorCalls ?? 0)
  };
};

const fetchSplit = async ({ chart, slice, nodeFilter }) =>
  readCategoryRows(
    await fetchNodeAggregate({ ...slice, node_id: chart.nodeId, output_key: chart.outputKey, node_filter: nodeFilter })
  );

/** Разбивка значения по второму измерению: итоги типа или типы итога. */
const useSplit = ({ chart, slice, nodeFilter, errorText }) => {
  const params = useMemo(() => ({ chart, slice, nodeFilter }), [chart, slice, nodeFilter]);
  const { data, isLoading, error } = useNodeRequest({
    fetcher: fetchSplit,
    params,
    errorText,
    enabled: Boolean(nodeFilter)
  });

  return {
    rows: nodeFilter && data ? data : [],
    isLoading: Boolean(nodeFilter) && (isLoading || (!data && !error)),
    error
  };
};

const NextStepHeader = ({ eyebrow, title, description, onCollapse, trackId }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">{eyebrow}</p>
      <h3 className="mt-1 text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
    <button
      type="button"
      data-ev={trackId}
      onClick={onCollapse}
      className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <X className="h-4 w-4" /> Свернуть
    </button>
  </div>
);

/** Следующий шаг разбора: разбивка выбранного значения по второму измерению. */
const SplitCard = ({ eyebrow, title, description, onCollapse, trackId, split, loadingText, emptyText, footnote, listProps }) => (
  <Card className="min-w-0 p-5 sm:p-6">
    <NextStepHeader eyebrow={eyebrow} title={title} description={description} onCollapse={onCollapse} trackId={trackId} />
    {split.error && <ErrorText>{split.error}</ErrorText>}
    {!split.error && split.isLoading && <MutedText>{loadingText}</MutedText>}
    {!split.error && !split.isLoading && split.rows.length === 0 && <MutedText>{emptyText}</MutedText>}
    {!split.error && !split.isLoading && split.rows.length > 0 && <CategoryList items={split.rows} {...listProps} />}
    <p className="mt-3 text-xs text-muted-foreground">{footnote}</p>
  </Card>
);

/** Разбор среза, в котором выбрано одно значение: только динамика сотрудников. */
const ScopeBreakdown = ({ dateRange, callType, nodeFilter, scopeTitle, shareDescription }) => {
  const breakdown = useEmployeeBreakdown({ dateRange, callType, nodeFilter });

  return (
    <EmployeeComparisonTrend
      dateRange={dateRange}
      breakdown={breakdown}
      scopeTitle={scopeTitle}
      shareDescription={shareDescription}
    />
  );
};

/** Разбор пары «тип → итог»: карточки сотрудников считаются по одному и тому же ответу сервера. */
const PairDetails = ({ dateRange, callType, typeChart, outcomeChart, pair, mode, onCollapse, onOpenCallJournal }) => {
  const panels = usePanelSettings();
  const showEmployees = panels.isVisible(PANEL_IDS['ui:call-paths.employees']);
  const showQuality = panels.isVisible(PANEL_IDS['ui:call-paths.quality']);
  const showCalls = panels.isVisible(PANEL_IDS['ui:call-paths.calls']);
  const showTrend = panels.isVisible(PANEL_IDS['ui:call-paths.pair-trend']);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
  const nodeFilter = pairFilter(typeChart, outcomeChart, pair.type.code, pair.outcome.code);
  const breakdown = useEmployeeBreakdown({
    dateRange,
    callType,
    nodeFilter,
    denominatorFilter: categoryFilter(typeChart, pair.type.code),
    // Список звонков берёт из разбора только перечень сотрудников для своего фильтра.
    enabled: showEmployees || showQuality || showCalls
  });

  return (
    <>
      <Card className="min-w-0 p-5 sm:p-6">
        <NextStepHeader
          eyebrow={`Выбранная пара · ${mode === 'outcomes' ? 'Итог → Тип' : 'Тип → Итог'}`}
          title={`${pair.type.label} → ${pair.outcome.label}`}
          description={`${formatCount(pair.calls)} из ${formatCount(pair.type.calls)} звонков типа «${pair.type.label}» · ${formatPercent(pair.calls, pair.type.calls)}`}
          onCollapse={onCollapse}
          trackId="call_paths.pair.collapse"
        />
      </Card>
      {showTrend && (
        <PairTrendChart dateRange={dateRange} callType={callType} typeChart={typeChart} outcomeChart={outcomeChart} pair={pair} />
      )}
      {showEmployees && (
        <EmployeeComparisonTrend
          dateRange={dateRange}
          breakdown={breakdown}
          scopeTitle={`${pair.type.label} → ${pair.outcome.label}`}
          shareDescription="Доля звонков с этим итогом среди звонков выбранного типа у каждого сотрудника за период."
        />
      )}
      {(showQuality || showCalls) && (
        <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          {showQuality && (
            <EmployeeQualityCard
              breakdown={breakdown}
              pairCalls={pair.calls}
              selectedEmployeeId={selectedEmployeeId}
              onSelectEmployee={setSelectedEmployeeId}
            />
          )}
          {showCalls && (
            <PairCallsCard
              dateRange={dateRange}
              callType={callType}
              nodeFilter={nodeFilter}
              employees={breakdown.employees}
              selectedEmployeeId={selectedEmployeeId}
              onSelectEmployee={setSelectedEmployeeId}
              onOpenCallJournal={onOpenCallJournal}
            />
          )}
        </div>
      )}
    </>
  );
};

const CallTypesContent = ({ dateRange, callType, typeChart, outcomeChart, onOpenCallJournal }) => {
  const panels = usePanelSettings();
  const showEmployees = panels.isVisible(PANEL_IDS['ui:call-paths.employees']);
  const [mode, setMode] = useState('types');
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [selectedOutcomeCode, setSelectedOutcomeCode] = useState('');

  const slice = useMemo(
    () => ({
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd'),
      call_type: callType === 'all' ? undefined : callType
    }),
    [dateRange, callType]
  );
  const overviewParams = useMemo(() => ({ typeChart, outcomeChart, slice }), [typeChart, outcomeChart, slice]);
  const { data: overview, isLoading, error: overviewError } = useNodeRequest({
    fetcher: fetchOverview,
    params: overviewParams,
    errorText: 'Не удалось загрузить типы и итоги звонков.'
  });
  const isOverviewPending = isLoading || (!overview && !overviewError);

  const types = overview?.types ?? [];
  const outcomes = overview?.outcomes ?? [];
  const selectedType = types.find(({ code }) => code === selectedTypeCode);
  const selectedOutcome = outcomes.find(({ code }) => code === selectedOutcomeCode);
  const typeFilter = selectedType ? categoryFilter(typeChart, selectedType.code) : '';
  const outcomeFilter = selectedOutcome ? categoryFilter(outcomeChart, selectedOutcome.code) : '';
  const isTypesMode = mode === 'types';

  const typeOutcomes = useSplit({
    chart: outcomeChart,
    slice,
    nodeFilter: isTypesMode ? typeFilter : '',
    errorText: 'Не удалось загрузить итоги этого типа.'
  });
  const outcomeTypes = useSplit({
    chart: typeChart,
    slice,
    nodeFilter: isTypesMode ? '' : outcomeFilter,
    errorText: 'Не удалось загрузить типы с этим итогом.'
  });

  const pair = resolvePair({
    mode,
    type: selectedType,
    outcome: selectedOutcome,
    typeOutcomes: typeOutcomes.rows,
    outcomeTypes: outcomeTypes.rows
  });

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setSelectedTypeCode('');
    setSelectedOutcomeCode('');
  };
  // Первое измерение режима сбрасывает второе: разбивка другого значения выбор уже не содержит.
  const selectType = (code) => {
    setSelectedTypeCode(code);
    if (isTypesMode) {
      setSelectedOutcomeCode('');
    }
  };
  const selectOutcome = (code) => {
    setSelectedOutcomeCode(code);
    if (!isTypesMode) {
      setSelectedTypeCode('');
    }
  };

  const { panelId, emptyText } = OVERVIEW_PANELS[mode];
  const overviewRows = isTypesMode ? types : outcomes;
  const analyzed = (isTypesMode ? overview?.typesAnalyzed : overview?.outcomesAnalyzed) ?? 0;
  const headerPanelId = PANEL_IDS['ui:call-paths.header'];

  const headerElement = (
      <Card data-panel-id={PANEL_IDS['ui:call-paths.header']}>
        <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-1.5">
            <CardTitle>{panels.title(headerPanelId)}</CardTitle>
            <InfoHint text={panels.description(headerPanelId)} label="О разделе" trackId="call_paths.header.hint" />
          </div>
          <MetricToggle trackId="call_paths.header.mode" options={MODES} value={mode} onChange={switchMode} />
        </CardHeader>
      </Card>
  );

  // Обзор — один элемент сетки на две панели: в каждый момент видна одна из них, по режиму.
  const overviewElement = (
        <Card data-panel-id={panelId} className="min-w-0">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <CardTitle className="text-lg">{panels.title(panelId)}</CardTitle>
              <InfoHint text={panels.description(panelId)} label="О списке значений" trackId="call_paths.overview.hint" />
            </div>
            <span className="rounded-full bg-muted px-3 py-1.5 text-xs text-muted-foreground">
              Проанализировано: {formatCount(analyzed)} звонков
            </span>
          </CardHeader>
          <CardContent>
            {overviewError && <ErrorText>{overviewError}</ErrorText>}
            {!overviewError && isOverviewPending && <MutedText>Загружаем аналитику…</MutedText>}
            {!overviewError && !isOverviewPending && overviewRows.length === 0 && <MutedText>{emptyText}</MutedText>}
            {!overviewError && !isOverviewPending && overviewRows.length > 0 && (
              <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(260px,0.9fr)_minmax(0,1.1fr)]">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Доля от {formatCount(analyzed)} проанализированных звонков</p>
                  <CategoryList
                    items={overviewRows}
                    selectedCode={isTypesMode ? selectedTypeCode : selectedOutcomeCode}
                    onSelect={isTypesMode ? selectType : selectOutcome}
                    max={overviewRows[0]?.calls}
                    denominator={analyzed}
                    denominatorLabel="от проанализированных звонков"
                  />
                </div>
                <div className="min-w-0 border-t pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                  <AllCategoriesTrend
                    key={mode}
                    dateRange={dateRange}
                    callType={callType}
                    chart={isTypesMode ? typeChart : outcomeChart}
                    items={overviewRows}
                    emphasisCode={isTypesMode ? selectedTypeCode : selectedOutcomeCode}
                    kind={mode}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
  );

  // Цепочка разбора — один составной элемент: её карточки делят выбор пары и одну загрузку
  // разбора по сотрудникам, и разнести их по сетке значило бы переписать логику, а не вёрстку.
  const hasDrilldown = Boolean((isTypesMode && selectedType) || (!isTypesMode && selectedOutcome) || pair);
  const drilldownElement = hasDrilldown && (
    <div className="flex flex-col gap-6">
      {isTypesMode && selectedType && (
        <SplitCard
          eyebrow="Следующий шаг · Итоги выбранного типа"
          title={selectedType.label}
          description={`${formatCount(selectedType.calls)} звонков · выберите итог для разбора сотрудников, баллов и звонков.`}
          onCollapse={() => selectType('')}
          trackId="call_paths.type.collapse"
          split={typeOutcomes}
          loadingText="Загружаем итоги типа…"
          emptyText="У этого типа нет определённых итогов."
          footnote="Доля итога считается среди звонков этого типа. У одного звонка может быть несколько итогов."
          listProps={{
            selectedCode: selectedOutcomeCode,
            onSelect: selectOutcome,
            max: selectedType.calls,
            denominator: selectedType.calls,
            denominatorLabel: 'звонков этого типа'
          }}
        />
      )}

      {!isTypesMode && selectedOutcome && (
        <SplitCard
          eyebrow="Следующий шаг · Типы с выбранным итогом"
          title={selectedOutcome.label}
          description={`${formatCount(selectedOutcome.calls)} звонков с этим итогом · выберите тип для разбора сотрудников, баллов и звонков.`}
          onCollapse={() => selectOutcome('')}
          trackId="call_paths.outcome.collapse"
          split={outcomeTypes}
          loadingText="Загружаем типы с этим итогом…"
          emptyText="Для этого итога нет определённых типов."
          footnote="Доля типа считается среди звонков с выбранным итогом."
          listProps={{
            selectedCode: selectedTypeCode,
            onSelect: selectType,
            max: selectedOutcome.calls,
            denominator: selectedOutcome.calls,
            denominatorLabel: 'звонков с этим итогом'
          }}
        />
      )}

      {/* Условие на пару, а не на выбранный код: после смены периода или направления код может
          остаться в состоянии, а значения в новой разбивке уже нет, и ниже стало бы пусто. */}
      {showEmployees && isTypesMode && selectedType && !pair && !typeOutcomes.isLoading && (
        <ScopeBreakdown
          key={`type-${selectedType.code}`}
          dateRange={dateRange}
          callType={callType}
          nodeFilter={typeFilter}
          scopeTitle={`Тип «${selectedType.label}»`}
          shareDescription="Доля звонков этого типа среди всех звонков каждого сотрудника за период."
        />
      )}
      {showEmployees && !isTypesMode && selectedOutcome && !pair && !outcomeTypes.isLoading && (
        <ScopeBreakdown
          key={`outcome-${selectedOutcome.code}`}
          dateRange={dateRange}
          callType={callType}
          nodeFilter={outcomeFilter}
          scopeTitle={`Итог «${selectedOutcome.label}»`}
          shareDescription="Доля звонков с этим итогом среди всех звонков каждого сотрудника за период."
        />
      )}

      {pair && (
        <PairDetails
          key={pair.key}
          dateRange={dateRange}
          callType={callType}
          typeChart={typeChart}
          outcomeChart={outcomeChart}
          pair={pair}
          mode={mode}
          onCollapse={() => (isTypesMode ? selectOutcome('') : selectType(''))}
          onOpenCallJournal={onOpenCallJournal}
        />
      )}
    </div>
  );

  const entries = [
    { id: PANEL_IDS['ui:call-paths.header'], element: headerElement },
    { id: 'ui:call-paths.overview', element: overviewElement },
    drilldownElement && { id: 'ui:call-paths.drilldown', element: drilldownElement }
  ].filter(Boolean);

  return (
    <SectionGrid
      tab="call-paths"
      entries={entries}
      defaults={CALL_PATHS_DEFAULTS}
      placeholders={DRILLDOWN_PLACEHOLDER}
    />
  );
};

/**
 * Раздел «Типы и итоги звонков»: разбор от типа обращения к итогу или наоборот, до сотрудников,
 * баллов и конкретных звонков. Работает на выходах `call_type` и `call_outcome` активного пакета.
 */
const CallTypesOutcomes = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const panels = usePanelSettings();
  const { catalog, isActive, isLoading, error, reload } = useNodeCatalog();
  const { typeChart, outcomeChart } = useMemo(() => findCallCharts(catalog), [catalog]);

  if (isLoading) {
    return <Card className="p-6 text-sm text-muted-foreground">Загружаем типы и итоги звонков…</Card>;
  }

  if (error) {
    return (
      <Card className="p-6 text-sm">
        {error}{' '}
        <button type="button" data-ev="call_paths.catalog.retry" className="ml-2 text-primary underline" onClick={reload}>
          Повторить
        </button>
      </Card>
    );
  }

  if (!isActive || !typeChart || !outcomeChart) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold">{panels.title(PANEL_IDS['ui:call-paths.header'])}</h2>
        <p className="mt-2 text-sm text-muted-foreground">В активном наборе анализа нужны выходы «Тип обращения» и «Итог звонка».</p>
      </Card>
    );
  }

  if (!dateRange?.from || !dateRange?.to) {
    return <Card className="p-6 text-sm text-muted-foreground">Выберите период.</Card>;
  }

  return (
    <CallTypesContent
      dateRange={dateRange}
      callType={callType}
      typeChart={typeChart}
      outcomeChart={outcomeChart}
      onOpenCallJournal={onOpenCallJournal}
    />
  );
};

export default CallTypesOutcomes;
